import type { QuestionSpec } from './shared-responder-v2-core-thread.types';

/**
 * The author's caption for one option, or `''` when they never named it.
 *
 * Identity lives in `optionValues` and display text in `optionLabels`, because
 * a label is free author text: two options may carry the same one, and an
 * option may carry none. A surface that has something else to show for an
 * unnamed option — the image card shows its picture — reads this; a surface
 * whose only content is the text reads `resolveOptionDisplayLabel`.
 */
export function resolveOptionCaption(
  spec: QuestionSpec,
  index: number
): string {
  return spec.optionLabels?.[index]?.trim() ?? '';
}

/**
 * The visible text for one option, never empty while the option exists.
 *
 * An unnamed option shows its value rather than a blank row, which is also what
 * a hand-built spec relies on: a fixture whose values are already readable text
 * ships no `optionLabels` at all.
 */
export function resolveOptionDisplayLabel(
  spec: QuestionSpec,
  index: number
): string {
  return resolveOptionCaption(spec, index) || (spec.optionValues[index] ?? '');
}

/** Every option's visible text, index-aligned with `spec.optionValues`. */
export function resolveOptionDisplayLabels(
  spec: QuestionSpec
): readonly string[] {
  return spec.optionValues.map((_, index) =>
    resolveOptionDisplayLabel(spec, index)
  );
}

/**
 * The picture an `imageChoice` option renders, or `null` when it has none.
 *
 * Kept apart from `optionValues` so one uploaded image can back two options
 * without the two sharing an identity.
 */
export function resolveOptionImageUrl(
  spec: QuestionSpec,
  index: number
): string | null {
  return spec.optionImageUrls?.[index]?.trim() || null;
}

export function isOfferedOptionValue(
  value: string,
  spec: QuestionSpec
): boolean {
  return spec.optionValues.includes(value);
}

export function resolveOfferedOptionValues(
  values: readonly string[],
  spec: QuestionSpec
): readonly string[] {
  return [
    ...new Set(values.filter((value) => isOfferedOptionValue(value, spec))),
  ];
}

export function pickSingleSpokenValue<TValue>(
  values: readonly TValue[]
): TValue | undefined {
  return values.at(-1);
}
